import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

type SetupErrorReporterArgs = {
	url: string;
	environment: string;
	releaseVersion: string;
	tracingEnabled?: boolean;
	replayEnabled?: boolean;
	replaysSessionSampleRate?: string;
	replaysOnErrorSampleRate?: string;
	tracesSampleRate?: string;
	ignoreErrorStrings?: string[];
	ignoreErrorRegexStrings?: string[];
	debugMode?: boolean;
	filterThirdPartyErrors?: boolean;
	whitelistedEndpoints?: string[];
};

/**
 * Setup the error reporter with the given configuration.
 * @param config Configuration object for the error reporter
 * @param config.url The URL to send the error reports to.
 * @param config.environment The environment the application is running in.
 * @param config.releaseVersion The version of the application.
 * @param config.tracingEnabled Whether to enable tracing or not.
 * @param config.replayEnabled Whether to enable session replay or not.
 * @param config.replaysSessionSampleRate Sample rate for session replays (as string)
 * @param config.replaysOnErrorSampleRate Sample rate for error replays (as string)
 * @param config.ignoreErrorStrings List of strings to ignore errors by.
 * @param config.ignoreErrorRegexStrings Regex string to ignore errors by.
 * @param config.debugMode Whether to enable debug mode or not. Debug mode will log more information to the console and send more data to Sentry.
 * @param config.filterThirdPartyErrors Whether to filter third party errors or not.
 * @param config.whitelistedEndpoints Comma separated list of endpoints to ignore errors from.
 */
export function setupErrorReporter({
	url,
	environment,
	releaseVersion,
	tracingEnabled = false,
	replayEnabled = false,
	filterThirdPartyErrors = true,
	replaysSessionSampleRate = '0',
	replaysOnErrorSampleRate = '0',
	tracesSampleRate = '0',
	ignoreErrorRegexStrings = [],
	ignoreErrorStrings = [],
	debugMode = false,
	whitelistedEndpoints = []
}: SetupErrorReporterArgs) {
	const integrations: Sentry.BrowserOptions['integrations'] = [];

	if (filterThirdPartyErrors) {
		integrations.push(
			Sentry.thirdPartyErrorFilterIntegration({
				// Specify the application keys that you specified in the Sentry bundler plugin
				filterKeys: ['@komigen-web-frontend'],
				// Defines how to handle errors that contain third party stack frames.
				// Possible values are:
				// - 'drop-error-if-contains-third-party-frames'
				// - 'drop-error-if-exclusively-contains-third-party-frames'
				// - 'apply-tag-if-contains-third-party-frames'
				// - 'apply-tag-if-exclusively-contains-third-party-frames'
				behaviour: 'drop-error-if-contains-third-party-frames'
			})
		);
	}

	if (tracingEnabled) {
		integrations.push(
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes
			})
		);
	}

	if (replayEnabled) {
		integrations.push(
			Sentry.replayIntegration({
				maskAllText: false,
				maskAllInputs: false
			})
		);
	}

	const ignoreErrorsRegex = ignoreErrorRegexStrings.map(regex => new RegExp(regex));
	const ignoreErrors = [...ignoreErrorsRegex, ...ignoreErrorStrings];

	Sentry.init({
		dsn: url,
		integrations,
		debug: debugMode,

		environment,
		release: releaseVersion,

		// Tracing
		tracesSampleRate: tracesSampleRate ? parseFloat(tracesSampleRate) || 0 : 0,

		// Session Replay
		replaysSessionSampleRate: replaysSessionSampleRate ? parseFloat(replaysSessionSampleRate) || 0 : 0,
		replaysOnErrorSampleRate: replaysOnErrorSampleRate ? parseFloat(replaysOnErrorSampleRate) || 0 : 0,

		// Depth of context sent to sentry
		normalizeDepth: 10,
		ignoreErrors,
		beforeSend: event => {
			const headers = event?.request?.headers;
			// ignore errors originating from bot requests
			if (headers && headers['User-Agent'] && headers['User-Agent'].includes('HeadlessChrome')) {
				return null;
			}
			// Check if the extra data and the endpoint field exist
			if (event.extra && event.extra.endpoint) {
				const { endpoint } = event.extra;

				// If the endpoint is in our whitelist, discard the event
				if (typeof endpoint === 'string' && whitelistedEndpoints.includes(endpoint)) {
					// TODO - remove this before release
					console.log(`Sentry event for endpoint "${endpoint}" was skipped.`);
					return null; // Don't send the event
				}
			}

			return event;
		}
	});
}
